import { RoutePaths } from 'containers/Core/Routes'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useHistory } from 'react-router-dom'
import {
  NearbyTimeSlotsResponse,
  useLabScheduling,
} from 'hooks/useLabScheduling/useLabscheduling'
import useSearchParams from 'hooks/useSearchParams'
import { format, utcToZonedTime } from 'date-fns-tz'
import { LabSchedulingSource } from 'utils/amplitude.types'
import { LabAppointment } from 'types/appointments'

export type LabSchedulingScreen =
  | 'labSelect'
  | 'address'
  | 'timeslotMap'
  | 'timeslot'
  | 'confirm'
  | 'confirmation'
  | 'modify'
  | 'cancellation'

export type LabType = 'LabCorp' | 'Quest' | null

export enum validTimeZones {
  NEW_YORK = 'America/New_York',
  CHICAGO = 'America/Chicago',
  DENVER = 'America/Denver',
  LOS_ANGELES = 'America/Los_Angeles',
  ANCHORAGE = 'America/Anchorage',
  HONOLULU = 'Pacific/Honolulu',
}

export interface LabSchedulingState {
  selectedAddress: string
  selectedLabId: string
  selectedLabAddress: string
  availableDays: string[]
  selectedDate: string
  selectedTime: string
  timeSlotsResponse: NearbyTimeSlotsResponse | null
  displayTimezone: validTimeZones
  labTimezone: validTimeZones
}

interface LabAppointmentState {
  confirmationNumber: string
  confirmationUrl: string
  qrCode: string
}

interface AmplitudeLabSchedulingMetadata {
  patient_action_id: string
  lab_appointment_id: string
  days_since_nearest_slot: number
  source: LabSchedulingSource
  lab_appointment_time: string
}

interface LabSchedulingContextInterface {
  currentScreen: LabSchedulingScreen
  setCurrentScreen: (screen: LabSchedulingScreen) => void
  selectedLab: LabType
  setSelectedLab: (lab: LabType) => void
  previousScreen: () => void
  labState: LabSchedulingState
  setLabState: (state: Partial<LabSchedulingState>) => void
  labAppointmentState: LabAppointmentState
  setLabAppointmentState: (state: Partial<LabAppointmentState>) => void
  isModifyFlow: boolean
  setIsModifyFlow: (state: boolean) => void
  isInitializing: boolean
  resetLabSchedulingState: () => void
  amplitudeLabSchedulingMetadata: AmplitudeLabSchedulingMetadata
  updateAmplitudeLabSchedulingMetadata: (
    state: Partial<AmplitudeLabSchedulingMetadata>
  ) => void
  resetSelectedTime: () => void
  populateAppointmentData: (appointment: any) => void
}

export const initialLabState: Readonly<LabSchedulingState> = {
  selectedAddress: '',
  selectedLabId: '-1',
  selectedLabAddress: '',
  availableDays: [],
  selectedDate: '',
  selectedTime: '',
  timeSlotsResponse: null,
  displayTimezone: validTimeZones.NEW_YORK,
  labTimezone: validTimeZones.NEW_YORK,
}

const initialLabAppointmentState: LabAppointmentState = {
  confirmationNumber: '',
  confirmationUrl: '',
  qrCode: '',
}

const initialAmplitudeLabSchedulingMetadata: AmplitudeLabSchedulingMetadata = {
  patient_action_id: '',
  lab_appointment_id: '',
  days_since_nearest_slot: 0,
  source: LabSchedulingSource.DASHBOARD_ACTION,
  lab_appointment_time: '',
}

interface LabSchedulingProviderProps {
  initialScreen?: LabSchedulingScreen
  children: React.ReactNode
}

export const LabSchedulingProvider: React.FC<LabSchedulingProviderProps> = ({
  initialScreen = 'labSelect',
  children,
}) => {
  const history = useHistory()
  const searchParams = useSearchParams()
  const { labAppointments } = useLabScheduling()

  const [currentScreen, setCurrentScreen] = useState<LabSchedulingScreen>(
    initialScreen
  )
  const [selectedLab, setSelectedLab] = useState<LabType>(null)
  const [labState, setLabState] = useState<LabSchedulingState>(initialLabState)
  const [
    labAppointmentState,
    setLabAppointmentState,
  ] = useState<LabAppointmentState>(initialLabAppointmentState)
  const [isModifyFlow, setIsModifyFlow] = useState(false)
  const [isInitializing, setIsInitializing] = useState(false)
  const [
    amplitudeLabSchedulingMetadata,
    setAmplitudeLabSchedulingMetadata,
  ] = useState<AmplitudeLabSchedulingMetadata>(
    initialAmplitudeLabSchedulingMetadata
  )

  const updateLabState = useCallback(
    (newState: Partial<LabSchedulingState>) => {
      setLabState((prevState) => ({
        ...prevState,
        ...newState,
      }))
    },
    []
  )

  const updateLabAppointmentState = useCallback(
    (newState: Partial<LabAppointmentState>) => {
      setLabAppointmentState((prevState) => ({
        ...prevState,
        ...newState,
      }))
    },
    []
  )

  const updateAmplitudeLabSchedulingMetadata = useCallback(
    (newState: Partial<AmplitudeLabSchedulingMetadata>) => {
      setAmplitudeLabSchedulingMetadata((prevState) => ({
        ...prevState,
        ...newState,
      }))
    },
    []
  )

  const resetSelectedTime = useCallback(() => {
    setLabState((prevState) => ({
      ...prevState,
      selectedTime: '',
      selectedLabId: '-1',
      selectedLabAddress: '',
    }))
  }, [])

  const populateAppointmentData = useCallback(
    (appointment: LabAppointment) => {
      const {
        confirmationNumber,
        confirmationUrl,
        qrCode,
        labLocation,
        scheduledAt,
        timezone,
        addressSearched,
        labProvider,
        labCode,
        id,
      } = appointment

      const zonedDate = utcToZonedTime(new Date(scheduledAt), timezone)
      const dateString = format(zonedDate, 'yyyy-MM-dd', {
        timeZone: timezone,
      })
      const timeString = format(zonedDate, 'HH:mm', { timeZone: timezone })

      setSelectedLab(labProvider)
      updateLabState({
        selectedLabAddress: labLocation,
        selectedAddress: addressSearched,
        selectedLabId: labCode,
        selectedDate: dateString,
        selectedTime: timeString,
        labTimezone: timezone,
      })
      updateLabAppointmentState({
        confirmationNumber,
        confirmationUrl: confirmationUrl || '',
        qrCode,
      })
      updateAmplitudeLabSchedulingMetadata({
        lab_appointment_id: id,
        lab_appointment_time: scheduledAt,
      })
    },
    [
      updateLabState,
      updateLabAppointmentState,
      updateAmplitudeLabSchedulingMetadata,
    ]
  )

  const resetLabSchedulingState = useCallback(() => {
    setCurrentScreen(initialScreen)
    setSelectedLab(null)
    setLabState(initialLabState)
    setLabAppointmentState(initialLabAppointmentState)
    setIsModifyFlow(false)
    setIsInitializing(false)
  }, [initialScreen])

  useEffect(() => {
    const handleAmplitudeData = () => {
      const metadata = {
        patient_action_id: searchParams.get('patientActionId') ?? '',
        patient_action_definition:
          searchParams.get('patientActionDefinition') ?? '',
        lab_appointment_id: searchParams.get('labAppointmentId') ?? '',
      }

      updateAmplitudeLabSchedulingMetadata(metadata)
    }

    handleAmplitudeData()
  }, [searchParams])

  useEffect(() => {
    const handleUrlParams = async () => {
      if (!labAppointments) {
        return
      }

      setIsInitializing(true)
      const labAppointmentId = searchParams.get('labAppointmentId')
      const confirmationNumber = searchParams.get('confirmationNumber')

      try {
        if (!!labAppointmentId) {
          updateAmplitudeLabSchedulingMetadata({
            lab_appointment_id: labAppointmentId,
          })
        }

        if (!!labAppointmentId && !!confirmationNumber) {
          const appointmentData = labAppointments.find(
            (appt) => appt.id === labAppointmentId
          )

          if (!appointmentData) {
            console.error('Unable to find appointment with matching id')
            return
          }

          populateAppointmentData(appointmentData)
          setCurrentScreen('modify')
        } else if (!!labAppointmentId) {
          const appointmentData = labAppointments.find(
            (appt) => appt.id === labAppointmentId
          )

          if (!appointmentData) {
            console.error('Unable to find appointment with matching id')
            return
          }

          populateAppointmentData(appointmentData)
          setCurrentScreen('confirmation')
        }
      } catch (error) {
        console.error('Error occurred initializing url params', error)
      } finally {
        setIsInitializing(false)
      }
    }

    handleUrlParams()
  }, [labAppointments, updateLabState, updateLabAppointmentState, searchParams])

  const previousScreen = useCallback(() => {
    const labAppointmentId = searchParams.get('labAppointmentId')
    const confirmationNumber = searchParams.get('confirmationNumber')

    switch (currentScreen) {
      case 'labSelect':
        history.push(RoutePaths.DASHBOARD)
        break
      case 'address':
        if (labAppointmentId && confirmationNumber) {
          window.location.href = `${RoutePaths.LAB_SCHEDULING}?labAppointmentId=${labAppointmentId}&confirmationNumber=${confirmationNumber}`
        } else {
          setCurrentScreen('labSelect')
        }

        break
      case 'timeslotMap':
        setCurrentScreen('address')
        break
      case 'confirm':
        setCurrentScreen('timeslotMap')
        break
      default:
        history.push(RoutePaths.DASHBOARD)
        break
    }
  }, [currentScreen, history, searchParams])

  return (
    <LabSchedulingContext.Provider
      value={{
        currentScreen,
        setCurrentScreen,
        selectedLab,
        setSelectedLab,
        previousScreen,
        labState,
        setLabState: updateLabState,
        labAppointmentState,
        setLabAppointmentState: updateLabAppointmentState,
        isModifyFlow,
        setIsModifyFlow,
        isInitializing,
        resetLabSchedulingState,
        amplitudeLabSchedulingMetadata,
        updateAmplitudeLabSchedulingMetadata,
        resetSelectedTime,
        populateAppointmentData,
      }}
    >
      {children}
    </LabSchedulingContext.Provider>
  )
}

export const LabSchedulingContext = createContext<LabSchedulingContextInterface>(
  {} as LabSchedulingContextInterface
)

export const useLabSchedulingContext = (): LabSchedulingContextInterface =>
  useContext(LabSchedulingContext)
